import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { 
  subscribeToCursors, 
  initializeCursorPresence, 
  cleanupCursorPresence,
  updateCursorPosition,
  removeCursor,
  setupCursorCleanup
} from '../services/cursors';
import { generateUserColor, generateDisplayName, throttle, calculateDistance } from '../utils/helpers';

/**
 * Custom hook for managing real-time cursor positions and user presence
 * @returns {Object} cursors - Object containing cursor data keyed by userId
 */
export function useCursors() {
  const { currentUser } = useAuth();
  const [cursors, setCursors] = useState({});
  
  // Track last cursor position to avoid unnecessary updates
  const lastPositionRef = useRef({ x: 0, y: 0 });
  const userColorRef = useRef(null);
  const displayNameRef = useRef(null);

  // Throttled cursor update function (30ms intervals)
  const throttledCursorUpdate = useCallback(
    throttle(async (x, y) => {
      if (!currentUser?.uid || !userColorRef.current || !displayNameRef.current) return;
      
      const lastPos = lastPositionRef.current;
      const distance = calculateDistance(lastPos.x, lastPos.y, x, y);
      
      // Only update if position changed by more than 2 pixels
      if (distance > 2) {
        try {
          await updateCursorPosition(
            currentUser.uid, 
            x, 
            y, 
            displayNameRef.current, 
            userColorRef.current
          );
          lastPositionRef.current = { x, y };
        } catch (error) {
          console.error('Error updating cursor position:', error);
        }
      }
    }, 30), // 30ms throttle (33 FPS)
    [currentUser]
  );

  // Mouse move handler
  const handleMouseMove = useCallback((event) => {
    const x = event.clientX;
    const y = event.clientY;
    throttledCursorUpdate(x, y);
  }, [throttledCursorUpdate]);

  useEffect(() => {
    if (!currentUser) {
      // Clear cursors when user logs out
      setCursors({});
      return;
    }

    let unsubscribe = null;

    const initializeCursors = async () => {
      try {
        // Generate consistent color and display name for current user
        const userColor = generateUserColor(currentUser.uid);
        const displayName = generateDisplayName(currentUser);
        
        // Store in refs for use in throttled updates
        userColorRef.current = userColor;
        displayNameRef.current = displayName;

        // Set up cursor cleanup on disconnect (explicit setup)
        await setupCursorCleanup(currentUser.uid);
        
        // Initialize cursor presence for current user
        await initializeCursorPresence(currentUser.uid, displayName, userColor);
        
        // Subscribe to other users' cursors
        unsubscribe = subscribeToCursors((updatedCursors) => {
          setCursors(updatedCursors);
        }, currentUser.uid);
        
        // Add mouse move listener for cursor tracking
        document.addEventListener('mousemove', handleMouseMove);
        
        console.log('✅ Cursor tracking initialized for:', displayName);
      } catch (error) {
        console.error('❌ Error initializing cursor tracking:', error);
        // Set empty cursors on error to prevent app crash
        setCursors({});
      }
    };

    initializeCursors();

    // Cleanup function
    return () => {
      // Remove mouse move listener
      document.removeEventListener('mousemove', handleMouseMove);
      
      // Unsubscribe from cursors
      if (unsubscribe) {
        unsubscribe();
      }
      
      // Manually remove current user's cursor (immediate cleanup)
      if (currentUser?.uid) {
        removeCursor(currentUser.uid).catch(error => {
          console.warn('Warning: Failed to remove cursor:', error);
        });
        
        // Also call general cleanup
        cleanupCursorPresence(currentUser.uid).catch(error => {
          console.warn('Warning: Failed to cleanup cursor presence:', error);
        });
      }
      
      // Reset refs
      lastPositionRef.current = { x: 0, y: 0 };
      userColorRef.current = null;
      displayNameRef.current = null;
      
      console.log('🧹 Cursor tracking cleaned up');
    };
  }, [currentUser, handleMouseMove]);

  return cursors;
}


export default useCursors;
